import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Agreement from '@components/pages/c005/agreement';
import Stepper from '@components/parts/Stepper';
import { Box, Button, Card, Checkbox, Divider, FormControlLabel, FormGroup, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import dayjs from 'dayjs';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { registReservationApi } from '@apis/index';
import { MessageDialogState } from '@atom/DialogState';
import { LoadingState } from '@atom/LoadingState';

import { RegistReservationMutationVariables, registReservationType } from '../../../API';
import { ReservationState } from '../../../atom/ReservationState';
import { IS_DISP_FEE } from '../../../config/applicationConfig';
import PageBase from '../PageBase';

const TemporaryReservationConfirmPage = () => {
  /* 共通State */
  const setMessageDialogState = useSetRecoilState(MessageDialogState);
  const setLoading = useSetRecoilState(LoadingState);

  const [reservation, setReservation] = useRecoilState(ReservationState);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isUseFacilityChecked, setIsUseFacilityChecked] = useState<boolean>(false);
  const [isUsageFeeChecked, setIsUsageFeeChecked] = useState<boolean>(false);

  const title = reservation.areaName;
  const date = reservation.reservedStartTime;
  const dispDate = dayjs(date).format('YYYY年M月D日(dd)');

  const dispStartTime = dayjs(reservation.reservedStartTime).format('HH:mm');
  const dispEndTime = dayjs(reservation.reservedEndTime).format('HH:mm');
  const dispSectionList = reservation.sectionNameList.join(',');

  useEffect(() => {
    // リロードされた場合は利用目的登録画面に戻す
    if (reservation.facilityId === '') {
      // クエリストリング取得
      const facilityIdParam = searchParams.get('facilityId') ?? '';
      const areaIdParam = searchParams.get('areaId') ?? '';
      const dateParam = searchParams.get('date') ?? '';
      const titleParam = searchParams.get('title') ?? '';
      const startTimeParam = searchParams.get('startTime') ?? '';
      const endTimeParam = searchParams.get('endTime') ?? '';
      const sectionListParam = searchParams.get('sectionList') ?? '';
      navigate(
        `/c005/purposeregister?facilityId=${facilityIdParam}&areaId=${areaIdParam}&date=${dateParam}&title=${titleParam}&startTime=${startTimeParam}&endTime=${endTimeParam}&sectionList=${sectionListParam}`
      );
    }
  }, []);

  // 登録ボタン押下時の処理
  const register = async () => {
    setLoading(true);
    try {
      const filterEquipmentList = reservation.equipmentList?.map(({ equipmentId, equipmentName, num, unitPrice }) => ({
        equipmentId,
        equipmentName,
        number: num,
        unitPrice
      }));
      // API実行
      // API実行用パラメータ
      const tempPeriod = `${dispDate} ${dispStartTime}-${dispEndTime}`;
      const input: RegistReservationMutationVariables = {
        input: {
          facilityId: reservation.facilityId,
          areaId: reservation.areaId,
          sectionList: reservation.sectionIdList,
          adminFlag: reservation.adminFlag, // 固定
          groupId: reservation.groupId, // 固定
          groupName: reservation.groupName,
          reservedStartTime: reservation.reservedStartTime,
          reservedEndTime: reservation.reservedEndTime,
          usagePurpose: reservation.usagePurpose,
          usageType: reservation.usageType,
          usageDetails: reservation.usageDetails,
          usageRange: reservation.usageRange,
          personInCharge: reservation.personInCharge,
          email: reservation.email,
          tel: reservation.tel,
          usageContents: reservation.usageContents,
          numberOfTotalPeople: reservation.numberOfTotalPeople,
          numberOfElementaryMiddleSchoolStudents: reservation.numberOfElementaryMiddleSchoolStudents,
          numberOfElementaryHighSchoolStudents: reservation.numberOfElementaryHighSchoolStudents,
          numberOfOrdinaryPerson: reservation.numberOfOrdinaryPerson,
          numberOfSpectator: reservation.numberOfSpectator,
          isCollectFee: reservation.isCollectFee,
          isCommercialActivity: reservation.isCommercialActivity,
          isElectricityUtilization: reservation.isElectricityUtilization,
          electricityUtilizationStartTime: reservation.electricityUtilizationStartTime,
          electricityUtilizationEndTime: reservation.electricityUtilizationEndTime,
          equipmentList: filterEquipmentList,
          totalFee: reservation.totalFee,
          facilityFee: reservation.facilityFee,
          equipmentFee: reservation.selectedEquipmentTotal,
          paymentMethodsFlag: reservation.paymentMethodsFlag, // 固定
          reservationPeriod: tempPeriod,
          depositDate: reservation.depositDate
        }
      };
      const result: registReservationType = await registReservationApi(input);
      setReservation({
        ...reservation,
        reservationId: result.reservationId
      });

      // 登録完了後、遷移
      navigate('/c005/reservationcomplete');
    } catch (error) {
      console.error(error);
      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if (error instanceof Error) {
        message = error.message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageBase title="仮予約内容の確認" isButton={false}>
      <Box sx={{ mr: 1, ml: 1 }}>
        <Stepper activeStep={2} />
        <Grid container spacing={2}>
          <Grid xs={8}>
            <Typography>
              {dispDate} {dispStartTime}-{dispEndTime}
              <br />
              {title} {dispSectionList}
            </Typography>
          </Grid>
          <Grid xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() =>
                navigate(
                  `/c001/weeklyschedule/?facilityId=${reservation.facilityId}&areaId=${reservation.areaId}&title=${title}`
                )
              }
            >
              日付再選択
            </Button>
          </Grid>
          <Grid xs={12}>
            {/* 現在は常に金額非表示だが、今後表示が必要になった際に戻せるように残しておく */}
            {IS_DISP_FEE &&
              <Card sx={{ height: 'auto', backgroundColor: '#EEF1F4', p: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>施設利用料</Typography>
                  <Typography>{reservation.facilityFee}円</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>備品・設備</Typography>
                  <Typography>{reservation.selectedEquipmentTotal}円</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography>合計金額(税込)</Typography>
                  <Typography variant="h5">{reservation.totalFee}円</Typography>
                </Box>
              </Card>
            }
            <Card sx={{ height: 'auto', backgroundColor: '#EEF1F4', p: 1, mt: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>入金期限</Typography>
                <Typography>{reservation.depositDate}</Typography>
              </Box>
            </Card>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5">登録内容の確認</Typography>
              <Button
                onClick={() =>
                  navigate(
                    `/c005/purposeregister?facilityId=${reservation.facilityId}&areaId=${reservation.areaId}&date=${dayjs(reservation.reservedStartTime).format('YYYY-M-D')}&title=${reservation.areaName}&startTime=${dayjs(reservation.reservedStartTime).format('HH:mm')}&endTime=${dayjs(reservation.reservedEndTime).format('HH:mm')}&sectionList=${reservation.sectionIdList}`
                  )
                }
              >
                修正
              </Button>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>利用目的(大会名等)</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.usagePurpose}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>利用種別</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.usageType}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>内容</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.usageDetails}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>利用団体名</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.groupName}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>使用責任者</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.personInCharge}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>メールアドレス</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.email}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>電話番号(ハイフン不要)</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.tel}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>利用内容</Typography>
              <Typography sx={{ fontSize: '18px' }}>
                {reservation.usageContents === 'amateur' ? 'アマチュアスポーツ' : '上記以外の利用'}
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>入場料</Typography>
              <Typography sx={{ fontSize: '18px' }}>{!reservation.isCollectFee ? '徴収しない' : '徴収する'}</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>営利目的の利用か</Typography>
              <Typography sx={{ fontSize: '18px' }}>
                {!reservation.isCommercialActivity ? '営利を目的としない' : '営利を目的とする'}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '15px' }}>使用予定人数</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '18px' }}>小・中学生</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.numberOfElementaryMiddleSchoolStudents}人</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '18px' }}>高校生</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.numberOfElementaryHighSchoolStudents}人</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '18px' }}>一般</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.numberOfOrdinaryPerson}人</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '18px' }}>合計</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.numberOfTotalPeople}人</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography sx={{ fontSize: '18px' }}>観覧者</Typography>
              <Typography sx={{ fontSize: '18px' }}>{reservation.numberOfSpectator}人</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>電気利用</Typography>
              <Typography sx={{ fontSize: '18px' }}>
                {reservation.isElectricityUtilization ? '利用する' : '利用しない'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5">オプション</Typography>
              <Button
                onClick={() =>
                  navigate(
                    `/c005/equipmentrequest?facilityId=${reservation.facilityId}&areaId=${reservation.areaId}&date=${dayjs(reservation.reservedStartTime).format('YYYY-M-D')}&title=${reservation.areaName}&startTime=${dayjs(reservation.reservedStartTime).format('HH:mm')}&endTime=${dayjs(reservation.reservedEndTime).format('HH:mm')}&sectionList=${reservation.sectionIdList}`
                  )
                }
              >
                修正
              </Button>
            </Box>
            {reservation.equipmentList?.map((equipment: any) => (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '15px' }}>{equipment.equipmentName}</Typography>
                <Typography sx={{ fontSize: '18px' }}>
                  {IS_DISP_FEE && `${equipment.unitPrice}円×`}
                  {equipment.num}
                </Typography>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h5">お支払い方法</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '15px' }}>窓口現金払い</Typography>
              <Typography sx={{ fontSize: '18px' }}>利用施設の窓口でお支払い</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Divider />
            </Box>
            <Box>
              <Typography variant="h5">利用規約</Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <Typography>
                <Agreement />
              </Typography>
            </Box>
            <Box sx={{ mb: 1 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isUseFacilityChecked}
                      onChange={(event) => setIsUseFacilityChecked(event.target.checked)}
                    />
                  }
                  label="施設使用にあたっての留意事項に同意する"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isUsageFeeChecked}
                      onChange={(event) => setIsUsageFeeChecked(event.target.checked)}
                    />
                  }
                  label="利用料金の還付に同意する"
                />
              </FormGroup>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() =>
                  navigate(
                    `/c005/purposeregister?facilityId=${reservation.facilityId}&areaId=${reservation.areaId}&date=${dayjs(reservation.reservedStartTime).format('YYYY-M-D')}&title=${reservation.areaName}&startTime=${dayjs(reservation.reservedStartTime).format('HH:mm')}&endTime=${dayjs(reservation.reservedEndTime).format('HH:mm')}&sectionList=${reservation.sectionIdList}`
                  )
                }
              >
                内容を修正
              </Button>
              <Button
                variant="contained"
                fullWidth
                disabled={!(isUsageFeeChecked && isUseFacilityChecked)}
                onClick={register}
              >
                仮予約を確定
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </PageBase>
  );
};

export default TemporaryReservationConfirmPage;
